import { Typography } from "@mui/material";
import type { FormikValues } from "formik";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";

import { Column } from "@citrine/atoms/layout";
import type { LabelOrTranslationID } from "@citrine/lang/utils";

import type { TextFieldProps } from "#src/components/form";

import type { FormSliderProps } from "./slider";
import * as sx from "./slider-with-input.sx";

export type SliderWithInputProps<Shape extends FormikValues> = Omit<
  FormSliderProps<Shape>,
  "data-testid"
> & {
  inputTestId?: string;
  sliderTestId?: string;
  slotProps?: TextFieldProps["slotProps"] & {
    textField?: Omit<TextFieldProps, "name" | "slotProps" | "type">;
  };
} & LabelOrTranslationID;

export function SliderWithInput<Shape extends FormikValues>({
  sliderTestId,
  inputTestId,
  disabled,
  slotProps: { textField: textFieldProps, ...slotProps } = {},
  label,
  translationId,
  className,
  ...props
}: SliderWithInputProps<Shape>) {
  const renderedLabel = useMemo(
    () =>
      label ? (
        <>{label}</>
      ) : translationId ? (
        <Typography variant="body1">
          <FormattedMessage id={translationId} />
        </Typography>
      ) : null,
    [label, translationId],
  );
  return (
    <Column gap={1}>
      {renderedLabel}
      <sx.SliderAndTextFieldContainer>
        <sx.SliderContainer>
          <sx.Slider
            disabled={disabled}
            data-testid={sliderTestId}
            className={className}
            {...props}
          />
        </sx.SliderContainer>
        <sx.SliderTextfield
          data-testid={inputTestId}
          disabled={disabled}
          {...textFieldProps}
          slotProps={slotProps}
          name={props.name}
          type="number"
        />
      </sx.SliderAndTextFieldContainer>
    </Column>
  );
}
